import {
  BOOKING_WITH_EMPTY_EMAIL,
  BOOKING_WITH_INVALID_EMAIL,
  BOOKING_WITH_RES_DETAILS,
  CANCELLED_BOOKING,
  DEFAULT_BOOKING,
  INTEGRATION_TEST_BOOKING,
  INTEGRATION_TEST_CANCELLED_BOOKING,
  NO_CHECKLESS_PAYMENTS_BOOKING,
  NO_FEE_BOOKING,
  PER_RESERVATION_FEE_BOOKING,
  REDIRECT_TO_LIVE_CHECK,
  SPECIAL_REQUEST_STEP_1,
  SPECIAL_REQUEST_STEP_2,
  STARTED_BOOKING,
  UNENROLLED_BOOKING,
  UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA,
  UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA,
  UNENROLLMENT_FINAL_DATA,
  UNENROLLMENT_INITIAL_DATA,
  UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA,
  UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA,
  USHG_BOOKING,
  WAITLIST_BOOKING
} from 'mocks/data/shared/bookingIds';
import type {
  Booking,
  BookingResponse,
  CardOnFile,
  Fee,
  PaymentMethod,
  SavedPaymentMethod
} from 'reservations/booking/types';
import { RESERVATION, WAITPARTY } from 'reservations/booking/types';

export const generateBookingDetails = ({
  firstName = 'Jimmy',
  lastName = 'Jammer',
  email = 'jimmy.jammer@gmail.com',
  phone = '2125551212',
  phoneCode = '1',
  dateAndTime = new Date(Date.now() + 60 * 60 * 1000)
    .toISOString()
    .slice(0, 19),
  partySize = 2,
  note = '',
  bookingId = DEFAULT_BOOKING,
  state = 'Active',
  tags = [],
  dietaryTags = [],
  optInMarketingEmail = false,
  optInMarketingSms = false,
  hasGuestConfirmed = false,
  positionInQueue = 0,
  type = RESERVATION,
  externalReservationUrl = '',
  showSentEmailConfirmation = true
}: Partial<Booking> = {}): Booking => ({
  firstName,
  lastName,
  email,
  phone,
  phoneCode,
  dateAndTime,
  partySize,
  note,
  bookingId,
  state,
  tags,
  dietaryTags,
  optInMarketingEmail,
  optInMarketingSms,
  hasGuestConfirmed,
  positionInQueue,
  type,
  externalReservationUrl,
  showSentEmailConfirmation
});

export const generateBookingFee = ({
  shouldCollect = true,
  collectType = 'CardOnFile',
  calcType = 'PartySize',
  fee = 2500,
  cancelDeadlineMinutes = 1000
}: Partial<Fee> = {}): Fee => ({
  calcType,
  collectType,
  fee,
  shouldCollect,
  cancelDeadlineMinutes
});

const today = new Date();
export const cardExpYear = today.getFullYear() + 1;
export const cardExpMonth = today.getMonth() + 1;

export const generatePaymentMethod = ({
  card_type = 'Visa',
  card_last4 = '3294',
  card_expiration_month = cardExpMonth,
  card_expiration_year = cardExpYear
}: Partial<PaymentMethod> = {}): PaymentMethod => ({
  card_type,
  card_last4,
  card_expiration_month,
  card_expiration_year
});

export const generateSavedPaymentMethod = ({
  id = '12332',
  payment_method = generatePaymentMethod()
}: Partial<SavedPaymentMethod> = {}): SavedPaymentMethod => ({
  id,
  payment_method
});

export const generateBookingCardOnFile = ({
  opt_in_checkless_payments = true,
  tip_percentage = 20,
  saved_payment_method = generateSavedPaymentMethod()
}: Partial<CardOnFile> = {}): CardOnFile => ({
  opt_in_checkless_payments,
  tip_percentage,
  saved_payment_method
});

export const generateBookingResponse = ({
  booking = generateBookingDetails(),
  fee = generateBookingFee(),
  card_on_file = generateBookingCardOnFile()
}: Partial<BookingResponse> = {}): BookingResponse => ({
  booking,
  fee,
  card_on_file
});

export const bookingByBookingId: Record<string, any> = {
  [DEFAULT_BOOKING]: generateBookingResponse(),
  [BOOKING_WITH_EMPTY_EMAIL]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_EMPTY_EMAIL,
      email: '',
      showSentEmailConfirmation: false
    })
  }),
  [BOOKING_WITH_INVALID_EMAIL]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_INVALID_EMAIL,
      email: 'invalid email'
    })
  }),
  [CANCELLED_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: CANCELLED_BOOKING,
      state: 'Canceled'
    })
  }),
  [STARTED_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: STARTED_BOOKING,
      state: 'Seated'
    })
  }),
  [WAITLIST_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: WAITLIST_BOOKING,
      type: WAITPARTY,
      positionInQueue: 2
    })
  }),
  [NO_FEE_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: NO_FEE_BOOKING
    }),
    fee: generateBookingFee({
      shouldCollect: false
    }),
    card_on_file: null
  }),
  [NO_CHECKLESS_PAYMENTS_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: NO_CHECKLESS_PAYMENTS_BOOKING
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: false
    })
  }),
  [PER_RESERVATION_FEE_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: PER_RESERVATION_FEE_BOOKING
    }),
    fee: generateBookingFee({
      calcType: RESERVATION
    })
  }),
  [USHG_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: USHG_BOOKING,
      externalReservationUrl: 'some-external-url',
      showSentEmailConfirmation: false
    })
  }),
  [BOOKING_WITH_RES_DETAILS]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_RES_DETAILS,
      tags: ['Birthday'],
      dietaryTags: ['Vegan'],
      note: 'This is a special request'
    })
  }),
  [UNENROLLED_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: UNENROLLED_BOOKING
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: false
    })
  }),
  [INTEGRATION_TEST_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({ bookingId: INTEGRATION_TEST_BOOKING }),
    fee: generateBookingFee({
      calcType: RESERVATION
    })
  }),
  [INTEGRATION_TEST_CANCELLED_BOOKING]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: INTEGRATION_TEST_CANCELLED_BOOKING,
      state: 'Canceled'
    })
  }),
  [SPECIAL_REQUEST_STEP_1]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: SPECIAL_REQUEST_STEP_1,
      note: 'This is a new special request.'
    })
  }),
  [SPECIAL_REQUEST_STEP_2]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: SPECIAL_REQUEST_STEP_2,
      note: 'This is an updated special request.'
    })
  }),
  [UNENROLLMENT_INITIAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({ bookingId: UNENROLLMENT_INITIAL_DATA }),
    card_on_file: generateBookingCardOnFile(
      generateBookingCardOnFile({
        opt_in_checkless_payments: false,
        tip_percentage: 0
      })
    )
  }),
  [UNENROLLMENT_FINAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({ bookingId: UNENROLLMENT_FINAL_DATA }),
    card_on_file: generateBookingCardOnFile(
      generateBookingCardOnFile({
        opt_in_checkless_payments: true,
        tip_percentage: 22
      })
    )
  }),
  [UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: false
    })
  }),
  [UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: true,
      tip_percentage: 12
    })
  }),
  [UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: true,
      tip_percentage: 22
    })
  }),
  [UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: true,
      tip_percentage: 20
    })
  }),
  [REDIRECT_TO_LIVE_CHECK]: generateBookingResponse({
    booking: generateBookingDetails({
      bookingId: REDIRECT_TO_LIVE_CHECK
    }),
    card_on_file: generateBookingCardOnFile({
      opt_in_checkless_payments: true,
      tip_percentage: 18
    })
  })
};
